"use client";

import React from "react";
import { createRoot, Root } from "react-dom/client";

/* ========================
   MapWarning Component
======================== */
interface MapWarningProps {
  message: string;
}

export function MapWarning({ message }: MapWarningProps) {
  return (
    <div className="map-warnings">
      <div className="route-warning">{message}</div>
    </div>
  );
}

/* ========================
   Warning Manager
======================== */
interface WarningData {
  id: number;
  message: string;
}

// Active warnings array
const warnings: WarningData[] = [];

// Portal container and root
let warningPortal: HTMLElement | null = null;
let root: Root | null = null;

/**
 * Register a DOM container as the portal for map warnings.
 * Call this once from your map component.
 */
export function registerMapWarningPortal(container: HTMLElement) {
  warningPortal = container;
}

/**
 * Get or create a React root for rendering warnings
 */
function getRoot(): Root {
  if (!root) {
    if (!warningPortal) {
      console.warn(
        "MapWarning: portal not registered. Falling back to body."
      );
      warningPortal = document.body;
    }
    root = createRoot(warningPortal);
  }
  return root;
}

/**
 * Render all active warnings in the portal
 */
function renderWarnings() {
  const r = getRoot();
  r.render(
    <>
      {warnings.map((w) => (
        <MapWarning key={w.id} message={w.message} />
      ))}
    </>
  );
}

/**
 * Remove a warning by id
 */
function removeWarning(id: number) {
  const index = warnings.findIndex((w) => w.id === id);
  if (index !== -1) {
    warnings.splice(index, 1);
    renderWarnings();
  }
}

/**
 * Show a map warning above the map panel.
 * Can be called from anywhere once portal is registered.
 */
export function showMapWarning(message: string, timeout = 8000) {
  if (typeof window === "undefined") return;

  const id = Date.now() + Math.random();
  warnings.push({ id, message });
  renderWarnings();

  // Auto-remove after timeout
  setTimeout(() => removeWarning(id), timeout);
}
